import { ReactNode, useMemo } from 'react';
import dayjs from 'dayjs';
import { Box } from '@mui/material';
import { TNote } from '../../types';
import { NoteDetailsUI } from './ui';
import { useNotesContext } from '../../hooks/useNotesContext';
import { PriorityIcon } from '../ui/PriorityIcon';
import { NoteTypeData, priorityLabels } from '../../data/task-manager';
import { getInitials } from '@/utils/getInitials';
import { getAssignee } from '../../utils';
import { ICLFaultList, ICLFaultTypeText } from '../../data/icl';
import { Avatar } from '../ui/Avatar';
import { NotePriority } from '../../enums';

type Props = {
  note: TNote;
};

const formatDate = (value: Date) => {
  return dayjs(value).format('DD.MM.YYYY, HH:mm');
};

export function NoteDetails({ note }: Props) {
  const { tags, collaborators } = useNotesContext();

  const noteDetailsData = useMemo(() => {
    const details: { label: ReactNode; value: ReactNode }[] = [];

    if (note.type) {
      details.push({
        label: 'Type',
        value: (
          <>
            {NoteTypeData[note.type].color ? (
              <Box
                sx={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  background: NoteTypeData[note.type].color,
                }}
              />
            ) : null}

            <NoteDetailsUI.ValueText>{NoteTypeData[note.type].label}</NoteDetailsUI.ValueText>
          </>
        ),
      });
    }

    if (note.notesICLData?.faultType) {
      details.push({
        label: 'Fault Type',
        value: ICLFaultTypeText[note.notesICLData.faultType],
      });
    }

    if (note.notesICLData?.faultType && typeof note.notesICLData.faultListIndex === 'number') {
      details.push({
        label: 'Fault List',
        value: ICLFaultList[note.notesICLData.faultType][note.notesICLData.faultListIndex],
      });
    }

    if (note.tagId) {
      const tag = tags.find((t) => t.id === note.tagId);
      if (tag?.title) {
        details.push({
          label: 'Tag name',
          value: tag.title,
        });
      }

      if (tag?.description) {
        details.push({
          label: 'Description of tag',
          value: <NoteDetailsUI.ValueText dangerouslySetInnerHTML={{ __html: tag?.description }} />,
        });
      }
    }

    if (typeof note.notesICLData?.isEquipmentFailure === 'boolean') {
      details.push({
        label: 'Equipment Failure',
        value: note.notesICLData.isEquipmentFailure ? 'Yes' : 'No',
      });
    }

    if (note.notesICLData?.availableFrom) {
      details.push({
        label: 'Available From',
        value: formatDate(new Date(note.notesICLData.availableFrom)),
      });
    }

    if (note.priority) {
      details.push({
        label: 'Priority',
        value: (
          <>
            <PriorityIcon priority={note.priority as NotePriority} />
            <NoteDetailsUI.ValueText>
              {priorityLabels[note.priority as NotePriority]}
            </NoteDetailsUI.ValueText>
          </>
        ),
      });
    }

    if (note.dueDate) {
      details.push({
        label: 'Due Date',
        value: formatDate(new Date(note.dueDate)),
      });
    }

    if (note.assignee) {
      const assignee = getAssignee(note.assignee, collaborators);

      details.push({
        label: 'Assignee',
        value: (
          <>
            <Avatar alt={assignee.name} bgColor={assignee.color}>
              {getInitials(assignee.name)}
            </Avatar>
            <NoteDetailsUI.ValueText>{assignee.name}</NoteDetailsUI.ValueText>
          </>
        ),
      });
    }

    if (typeof note.backlogEnabled === 'boolean') {
      details.push({
        label: 'Backlog',
        value: note.backlogEnabled ? 'Yes' : 'No',
      });
    }

    return details;
  }, [note, tags, collaborators]);

  if (!noteDetailsData.length) return null;

  return (
    <NoteDetailsUI>
      {noteDetailsData.map((data) => (
        <NoteDetailsUI.Row key={data.label.toString()} label={data.label} value={data.value} />
      ))}
    </NoteDetailsUI>
  );
}
